import React, { useState, useEffect } from 'react';

/**
 * Componente Agnóstico de Formulário para o CRM PTS
 * @param {Array} fields - Esquema dos campos: [{ name: 'cnpj', label: 'CNPJ', type: 'text', required: true, colSpan: 6, options: [] }]
 * @param {Object} initialData - (Opcional) Dados para pré-preencher o form (Modo Edição)
 * @param {Function} onSubmit - Função chamada ao salvar, retorna os dados: (formData) => void
 * @param {Function} onCancel - Função chamada ao cancelar
 * @param {Boolean} isProcessing - Desabilita os botões e mostra estado de loading
 * @param {String} submitLabel - Texto do botão de salvar
 * @param {Function} onFieldChange - (NOVO) Callback executado a cada tecla: (name, value) => void
 */
const FormularioDinamico = ({ 
    fields, 
    initialData = {}, 
    onSubmit, 
    onCancel, 
    isProcessing = false,
    submitLabel = "Salvar",
    onFieldChange 
}) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});

    // Popula o formulário se houver dados iniciais (Edição) ou mescla novos campos dinâmicos preservando o estado
    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setFormData(initialData);
        } else {
            setFormData(prev => {
                const newData = { ...prev };
                fields.forEach(field => {
                    // Adiciona apenas campos que ainda não existem no state atual (impede de apagar o que o usuário já clicou)
                    if (field.type !== 'title' && newData[field.name] === undefined) {
                        newData[field.name] = field.type === 'select' && field.options?.length > 0 ? field.options[0].value : '';
                    }
                });
                return newData;
            });
        }
    }, [initialData, fields]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Notifica o Pai se necessário
        if (onFieldChange) onFieldChange(name, value);

        // Limpa o erro daquele campo ao digitar
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validação simples de campos obrigatórios
        const newErrors = {};
        fields.forEach(field => {
            if (field.type !== 'title' && field.required && !formData[field.name]) {
                newErrors[field.name] = 'Este campo é obrigatório';
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Se passou na validação, envia os dados para o componente Pai
        onSubmit(formData);
    };

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* GRID DE CAMPOS: Divide em 12 colunas virtuais */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '15px' }}>
                {fields.map(field => {
                    const gridColumn = `span ${field.colSpan || 12}`;
                    const hasError = !!errors[field.name];

                    if (field.type === 'title') {
                        return (
                            <div key={field.name} style={{ gridColumn, marginTop: '15px', borderBottom: '1px solid #e2e8f0', paddingBottom: '5px' }}>
                                <strong style={{ color: '#0369a1', fontSize: '0.8rem', textTransform: 'uppercase' }}>{field.label}</strong>
                            </div>
                        );
                    }

                    return (
                        <div key={field.name} style={{ gridColumn, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <label style={{ fontSize: '0.85rem', color: '#475569', fontWeight: '600' }}>
                                {field.label} {field.required && <span style={{ color: '#ef4444' }}>*</span>}
                            </label>

                            {/* RENDERIZADOR CONDICIONAL DE INPUTS */}
                            {field.type === 'select' ? (
                                <select 
                                    name={field.name}
                                    value={formData[field.name] || ''}
                                    onChange={handleChange}
                                    disabled={isProcessing || field.disabled}
                                    style={{
                                        padding: '10px 15px', borderRadius: '8px',
                                        border: `1px solid ${hasError ? '#ef4444' : '#cbd5e1'}`,
                                        background: field.disabled ? '#f1f5f9' : '#fff',
                                        color: '#0f172a', fontSize: '0.95rem', outline: 'none'
                                    }}
                                >
                                    {field.options?.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            ) : field.type === 'textarea' ? (
                                <textarea
                                    name={field.name}
                                    placeholder={field.placeholder || ''}
                                    value={formData[field.name] || ''}
                                    onChange={handleChange}
                                    disabled={isProcessing || field.disabled}
                                    rows={4}
                                    style={{
                                        padding: '10px 15px', borderRadius: '8px',
                                        border: `1px solid ${hasError ? '#ef4444' : '#cbd5e1'}`,
                                        background: field.disabled ? '#f1f5f9' : '#fff',
                                        color: '#0f172a', fontSize: '0.95rem', outline: 'none', resize: 'vertical'
                                    }}
                                />
                            ) : (
                                <input 
                                    type={field.type || 'text'}
                                    name={field.name}
                                    placeholder={field.placeholder || ''}
                                    value={formData[field.name] || ''}
                                    onChange={handleChange}
                                    disabled={isProcessing || field.disabled}
                                    style={{
                                        padding: '10px 15px', borderRadius: '8px',
                                        border: `1px solid ${hasError ? '#ef4444' : '#cbd5e1'}`,
                                        background: field.disabled ? '#f1f5f9' : '#fff',
                                        color: '#0f172a', fontSize: '0.95rem', outline: 'none'
                                    }}
                                />
                            )}
                            
                            {/* MENSAGEM DE ERRO */}
                            {hasError && <span style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: '500' }}>{errors[field.name]}</span>}
                        </div>
                    );
                })}
            </div>

            {/* BARRA DE AÇÕES (RODAPÉ DO FORMULÁRIO) */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                {onCancel && (
                    <button 
                        type="button" 
                        onClick={onCancel}
                        disabled={isProcessing}
                        style={{
                            padding: '10px 20px', borderRadius: '8px', background: '#fff', border: '1px solid #cbd5e1',
                            color: '#475569', fontSize: '0.9rem', fontWeight: '600', cursor: isProcessing ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Cancelar
                    </button>
                )}
                <button 
                    type="submit"
                    disabled={isProcessing}
                    style={{
                        padding: '10px 20px', borderRadius: '8px', background: isProcessing ? '#94a3b8' : '#0369a1', border: 'none',
                        color: '#fff', fontSize: '0.9rem', fontWeight: '700', cursor: isProcessing ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s'
                    }}
                >
                    {isProcessing ? 'Processando...' : submitLabel}
                </button>
            </div>

        </form>
    );
};

export default FormularioDinamico;